import React, { useContext, useState } from 'react';
import { ActionModal } from '../../../../components/Modal';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { t } from '../../../../../types/translation/Translator';
import { CompanyConnection } from '../../../../../types/companyConnection/companyConnection';
import { CompanyContext } from '../../../../../context/CompanyContext';
import useMutation from '../../../../../hooks/useMutation';
import {
  CompanyConnectionMutations,
  DeleteCompanyConnectionResponse,
  DeleteCompanyConnectionVariables,
  UpdateCompanyConnectionResponse,
  UpdateCompanyConnectionVariables,
} from '../../../../../graphql/companyConnection.graphql';
import { CompanyConnectionContext } from '../../../../../context/CompanyConnectionContext';
import { Button, ButtonVariant, DeleteButton } from '../../../../components/Button';
import CompanyConnectionInfoPane from '../Common/CompanyConnectionInfoPane';

interface UpdateCompanyConnectionModalProps {
  connection: CompanyConnection;

  open: boolean;
  setOpen: (open: boolean) => void;
}

export default function UpdateCompanyConnectionModal({ connection, open, setOpen }: UpdateCompanyConnectionModalProps) {
  const { currentCompany } = useContext(CompanyContext);
  const { companyConnections, setCompanyConnections } = useContext(CompanyConnectionContext);

  const [item, setItem] = useState<CompanyConnection>(
    new CompanyConnection(connection) ||
      new CompanyConnection({ companyId: currentCompany.id, parentId: currentCompany.id }),
  );

  const [update, { loading: updateLoading }] = useMutation<
    UpdateCompanyConnectionResponse,
    UpdateCompanyConnectionVariables
  >(CompanyConnectionMutations.update, {
    onCompleted: response => {
      const connection = response.updateCompanyConnection[0];
      setCompanyConnections(new Map(companyConnections).set(connection.id, new CompanyConnection(connection)));
      setOpen(false);
    },
  });

  const [remove, { loading: deleteLoading }] = useMutation<
    DeleteCompanyConnectionResponse,
    DeleteCompanyConnectionVariables
  >(CompanyConnectionMutations.remove, {
    onCompleted: response => {
      const updatedConnections = new Map(companyConnections);
      response.deleteCompanyConnection.forEach(conn => {
        updatedConnections.delete(conn.id);
      });
      setCompanyConnections(updatedConnections);
      setOpen(false);
      window.location.reload();
    },
  });

  const valid = () => {
    return item.companyId && item.types.length;
  };

  const handleUpdate = async () => {
    await update({ variables: { companyConnections: [item.forUpdate()] } });
  };

  const handleRemove = async () => {
    await remove({ variables: { companyConnections: [item.forDelete()] } });
  };

  const loading = updateLoading || deleteLoading;

  const footer = (
    <>
      <div className='grid grid-cols-2'>
        <div className='flex gap-2 align-middle'>
          <Button
            disabled={loading}
            text={t(TranslationKey.cancel).singular.label}
            variant={ButtonVariant.secondary}
            onClick={() => setOpen(false)}
          />
          <DeleteButton
            loading={deleteLoading}
            disabled={updateLoading}
            text={t(TranslationKey.delete).singular.label}
            variant={ButtonVariant.secondary}
            onClick={handleRemove}
          />
        </div>
        <div className='flex justify-end'>
          <Button
            loading={updateLoading}
            disabled={deleteLoading || !valid()}
            text={t(TranslationKey.save).singular.label}
            onClick={handleUpdate}
          />
        </div>
      </div>
    </>
  );

  return (
    <ActionModal open={open} setOpen={setOpen} title={t(TranslationKey.linkCompany).plural.upper} footer={footer}>
      <CompanyConnectionInfoPane item={item} setItem={setItem} loading={loading} />
    </ActionModal>
  );
}
